import React, { useState } from "react";
import clsx from "clsx";

import { ReactComponent as VideoDurationIcon } from "../../assets/svg/video-duration-icon.svg";
import { ReactComponent as ArrowDown } from "../../assets/svg/caret_down.svg";
import { ReactComponent as IncompleteMark } from "../../assets/svg/check-mark.svg";
import { ReactComponent as CompleteMark } from "../../assets/svg/check-circle.svg";
import { ReactComponent as PlayVideoIcon } from "../../assets/svg/play-video-icon.svg";
import { zeroPaddingText } from "../../libs/Util";
import Button from "../../atoms/Button";
import { VideoChapter, VideoChapterWork } from "../../state";

import styles from "./ChapterCard.module.scss";

interface ChapterCardProps {
  chapter: VideoChapter;
  retrospectiveWorkTitle?: string;
  isPlaying?: boolean;
  isInitialOpen?: boolean;
  handlePlayingButton: (id: number) => void;
}

const OPPAWorkItem = ({ work }: { work: VideoChapterWork }) => {
  return (
    <div className={styles.work}>
      <div className={styles.workTitleWrapper}>
        {work.is_completed ? <CompleteMark /> : <IncompleteMark />}
        <p className={styles.workTitle}>
          {work.title.includes("Before") ? "Before WORK" : "After WORK"}
        </p>
      </div>
      {work.one_page_portfolio_assessment_issues?.map(oppaIssue => (
        <div key={oppaIssue.id} className={styles.workQuestion}>
          <p>{oppaIssue.title}</p>
          {oppaIssue.one_page_portfolio_assessment_questions.map(
            oppaQuestion => (
              <p key={oppaQuestion.id}>{oppaQuestion.title}</p>
            ),
          )}
        </div>
      ))}
    </div>
  );
};

const IssueWorkItem = ({
  work,
  retrospectiveWorkTitle,
}: {
  work: VideoChapterWork;
  retrospectiveWorkTitle?: string;
}) => {
  return (
    <div className={styles.work}>
      <div className={styles.workTitleWrapper}>
        {work.is_completed ? <CompleteMark /> : <IncompleteMark />}
        <p className={styles.workTitle}>
          {work.title === retrospectiveWorkTitle
            ? "振り返り"
            : zeroPaddingText(work.title).replace("ワーク", "WORK")}
        </p>
      </div>
      {work.prompt && <p className={styles.workQuestion}>{work.prompt}</p>}
    </div>
  );
};

const ChapterCard = ({
  chapter,
  retrospectiveWorkTitle,
  isPlaying,
  isInitialOpen,
  handlePlayingButton,
}: ChapterCardProps) => {
  const [isOpen, setIsOpen] = useState(isInitialOpen);
  const chapterCompleted = chapter.works
    .filter(work => work.title.replace(/\s/g, "") !== "HOMEWORK")
    .every(work => work.is_completed);

  return (
    <div
      className={clsx(
        styles.container,
        chapterCompleted && styles.chapterCompleted,
      )}
    >
      <div
        className={clsx(
          styles.titleContainer,
          isOpen && styles.openedTitleContainer,
        )}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
      >
        <p className={styles.title}>{chapter.title}</p>
        <div className={styles.duration}>
          <VideoDurationIcon />
          <p>{chapter.duration}</p>
        </div>
        {isPlaying && <p className={styles.displayPlaying}>再生中</p>}
        <ArrowDown
          className={clsx(styles.arrowUp, isOpen && styles.arrowDown)}
        />
      </div>
      {isOpen && (
        <div className={styles.detailContainer}>
          <div className={styles.workContainer}>
            <hr />
            {chapter.works.map(work =>
              work.type === "oppa" ? (
                <OPPAWorkItem key={`oppa-${work.id}`} work={work} />
              ) : (
                <IssueWorkItem
                  key={`issue-${work.id}`}
                  work={work}
                  retrospectiveWorkTitle={retrospectiveWorkTitle}
                />
              ),
            )}
          </div>
          {!isPlaying && (
            <Button
              name="再生する"
              variant="normal"
              onClick={() => handlePlayingButton(chapter.id)}
              className={styles.playingButton}
            >
              <PlayVideoIcon />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ChapterCard;
